// w42 Sakura (sakura falls) — rose-on-cream wedding preview card.
//
// The full template opens on a blossom-strewn hero: a soft blush sky
// fading into warm cream, a swaying branch overlay, and petals drifting
// down past shimmering names. The catalog card compresses that into one
// still frame: a vertical gradient running soft pink -> warm cream from top
// to bottom, a scatter of falling petal dots, a thin blossom-pink ring
// monogram holding the couple's initials, and the couple's names in a
// dark-plum serif treatment with a rose rule + date line below. Uses the
// site's already-loaded serif stack (via `font`) — no new font import.
import { type LayoutCtx } from "./_shared";

// Deterministic falling petal dots — same positions/sizes on every render
// (no Math.random so SSR/CSR + repeated mounts stay visually stable).
// Scattered across the full card height so the "falling" motif reads even
// as a still frame.
const PETALS: Array<{ x: number; y: number; r: number; o: number }> = [
  { x: 12, y: 12, r: 2.6, o: 0.75 },
  { x: 28, y: 20, r: 2.2, o: 0.55 },
  { x: 46, y: 10, r: 3, o: 0.7 },
  { x: 62, y: 24, r: 2.4, o: 0.6 },
  { x: 80, y: 14, r: 2.8, o: 0.65 },
  { x: 90, y: 32, r: 2.2, o: 0.5 },
  { x: 18, y: 42, r: 2.6, o: 0.5 },
  { x: 36, y: 55, r: 2.2, o: 0.45 },
  { x: 58, y: 46, r: 2.8, o: 0.55 },
  { x: 74, y: 60, r: 2.4, o: 0.45 },
  { x: 24, y: 72, r: 2.6, o: 0.4 },
  { x: 50, y: 82, r: 2.2, o: 0.4 },
  { x: 68, y: 78, r: 3, o: 0.45 },
  { x: 86, y: 68, r: 2.2, o: 0.4 },
];

// Split "Timur & Madina" / "Jasur va Gulnora" / "Дилшод и Мадина" into
// initials joined with a middot, matching the design's splash monogram.
// Falls back to a single initial when only one name is present — no
// dangling separator.
function initials(names: string): string {
  const parts = names
    .split(/\s*[&+—]\s*|\s+(?:va|и)\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);
  const letters = parts.map((p) => p.charAt(0).toUpperCase());
  return letters.length > 1 ? `${letters[0]}·${letters[1]}` : letters[0] || "";
}

export function SakuraPreviewLayout({ names, date, accent, ink, font }: LayoutCtx) {
  const rose = accent || "#e58aa6";
  const cream = "#fdf6f0";
  const plum = ink || "#3f3340";
  const mono = initials(names);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        // Soft pink top -> warm cream base.
        background: `linear-gradient(180deg, #f3c3d3 0%, #f8d9e2 40%, ${cream} 100%)`,
        padding: "6%",
        display: "flex",
        overflow: "hidden",
      }}
    >
      {/* Falling petal dots. */}
      {PETALS.map((petal, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${petal.x}%`,
            top: `${petal.y}%`,
            width: petal.r * 2,
            height: petal.r * 2,
            borderRadius: "50% 0 50% 50%",
            background: rose,
            opacity: petal.o,
            transform: "rotate(-40deg)",
          }}
        />
      ))}

      <div
        style={{
          flex: 1,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${rose}33`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.38em",
            color: plum,
            marginTop: "4%",
            marginBottom: "8%",
            textShadow: `0 1px 4px ${cream}cc`,
          }}
        >
          TO'Y TAKLIFNOMASI
        </div>

        {/* Thin blossom-pink ring monogram. */}
        <div
          style={{
            width: "30%",
            aspectRatio: "1 / 1",
            borderRadius: "50%",
            marginBottom: "8%",
            display: "grid",
            placeItems: "center",
            background: "transparent",
            boxShadow: `inset 0 0 0 1.5px ${rose}`,
          }}
        >
          <span
            style={{
              fontFamily: font,
              fontSize: 13,
              letterSpacing: "0.06em",
              color: plum,
            }}
          >
            {mono}
          </span>
        </div>

        {/* Dark-plum serif names. */}
        <div
          style={{
            fontFamily: font,
            fontSize: 26,
            lineHeight: 1.15,
            letterSpacing: "0.01em",
            color: plum,
            textShadow: `0 1px 10px ${cream}aa`,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Rose rule. */}
        <div style={{ width: "34%", height: 1, background: `${rose}cc`, marginBottom: "6%" }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.3em",
            color: plum,
          }}
        >
          {date.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
